import { useState, useCallback, useRef } from "react";
import { useSources } from "../hooks/useSources";
import { createSource, updateSource, deleteSource, uploadSourceLogo, deleteSourceLogo } from "../api/sources";
import { resolveMediaUrl } from "../api/client";
import type { SourceEntry } from "../types";

interface RssFeedViewProps {
  /** Nur RSS-Feeds anzeigen (kein E-Mail). Für die User-Ansicht in der Sidebar. */
  rssOnly?: boolean;
}

export function RssFeedView({ rssOnly = false }: RssFeedViewProps) {
  const { sources, loading, refresh } = useSources();
  const rssFeeds = sources.filter((s) => s.type === "rss_feed");
  const emailSources = sources.filter((s) => s.type === "email_newsletter");

  // Neuen Feed anlegen
  const [showAdd, setShowAdd] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Editieren
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editUrl, setEditUrl] = useState("");

  // Logo Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoUploadId, setLogoUploadId] = useState<number | null>(null);

  const getFeedUrl = (source: SourceEntry): string => {
    try {
      const config = source.config_json ? JSON.parse(source.config_json) : {};
      return config.feed_url || "";
    } catch {
      return "";
    }
  };

  const handleAdd = useCallback(async () => {
    const url = newUrl.trim();
    if (!url) {
      setError("Bitte eine Feed-URL eingeben.");
      return;
    }
    try {
      new URL(url);
    } catch {
      setError("Bitte eine gültige URL eingeben (z.B. https://...)");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await createSource({
        type: "rss_feed",
        name: newName.trim() || url,
        config_json: JSON.stringify({ feed_url: url }),
        enabled: true,
      });
      setNewUrl("");
      setNewName("");
      setShowAdd(false);
      refresh();
    } catch (e: any) {
      setError(e.message || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  }, [newUrl, newName, refresh]);

  const handleDelete = useCallback(async (source: SourceEntry) => {
    if (!confirm(`"${source.name}" wirklich löschen?`)) return;
    try {
      await deleteSource(source.id);
      refresh();
    } catch (e: any) {
      alert("Fehler: " + (e.message || "Unbekannt"));
    }
  }, [refresh]);

  const handleToggle = useCallback(async (source: SourceEntry) => {
    try {
      await updateSource(source.id, { enabled: !source.enabled });
      refresh();
    } catch (e: any) {
      alert("Fehler: " + (e.message || "Unbekannt"));
    }
  }, [refresh]);

  const startEdit = useCallback((source: SourceEntry) => {
    setEditId(source.id);
    setEditName(source.name);
    setEditUrl(getFeedUrl(source));
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (editId === null) return;
    try {
      await updateSource(editId, {
        name: editName.trim(),
        config_json: JSON.stringify({ feed_url: editUrl.trim() }),
      });
      setEditId(null);
      refresh();
    } catch (e: any) {
      alert("Fehler: " + (e.message || "Unbekannt"));
    }
  }, [editId, editName, editUrl, refresh]);

  const handleLogoClick = useCallback((sourceId: number) => {
    setLogoUploadId(sourceId);
    fileInputRef.current?.click();
  }, []);

  const handleLogoFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || logoUploadId === null) return;

    try {
      await uploadSourceLogo(logoUploadId, file);
      refresh();
    } catch (err: any) {
      alert("Logo-Upload fehlgeschlagen: " + (err.message || "Unbekannt"));
    } finally {
      setLogoUploadId(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [logoUploadId, refresh]);

  const handleLogoDelete = useCallback(async (sourceId: number) => {
    try {
      await deleteSourceLogo(sourceId);
      refresh();
    } catch (err: any) {
      alert("Fehler: " + (err.message || "Unbekannt"));
    }
  }, [refresh]);

  const renderSourceCard = (source: SourceEntry, isEmail: boolean) => (
    <div key={source.id} className={`feed-card ${!source.enabled ? "feed-card-disabled" : ""}`}>
      {editId === source.id ? (
        /* Edit-Modus */
        <div className="feed-card-edit">
          <div className="feed-form-row">
            <div className="feed-form-field feed-form-field-name">
              <label>Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoFocus
              />
            </div>
            {!isEmail && (
              <div className="feed-form-field feed-form-field-url">
                <label>URL</label>
                <input
                  type="url"
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="feed-form-actions">
            <button className="btn-primary btn-sm" onClick={handleSaveEdit}>Speichern</button>
            <button className="btn-ghost btn-sm" onClick={() => setEditId(null)}>Abbrechen</button>
          </div>
        </div>
      ) : (
        /* Anzeige-Modus */
        <>
          {/* Logo-Bereich */}
          <div className="source-logo-area">
            {source.logo_url ? (
              <div className="source-logo-preview">
                <img src={resolveMediaUrl(source.logo_url!)} alt={`${source.name} Logo`} />
              </div>
            ) : (
              <div className="source-logo-placeholder">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
              </div>
            )}
            <div className="source-logo-actions">
              <button className="btn-ghost btn-xs" onClick={() => handleLogoClick(source.id)}>
                {source.logo_url ? "Ändern" : "Logo"}
              </button>
              {source.logo_url && (
                <button className="btn-ghost btn-xs" onClick={() => handleLogoDelete(source.id)}>
                  Entfernen
                </button>
              )}
            </div>
          </div>

          <div className="feed-card-info">
            <div className="feed-card-header">
              <span className={`feed-status-dot ${source.enabled ? "dot-active" : "dot-inactive"}`} />
              <h3 className="feed-card-name">{source.name}</h3>
              {isEmail && <span className="feed-type-badge">E-Mail</span>}
            </div>
            {!isEmail && <p className="feed-card-url">{getFeedUrl(source)}</p>}
            {isEmail && !source.logo_url && (
              <p className="feed-card-hint">Favicon wird automatisch aus der Absender-Domain geladen</p>
            )}
            <span className="feed-card-date">
              Erstellt: {new Date(source.created_at).toLocaleDateString("de-DE")}
            </span>
          </div>
          <div className="feed-card-actions">
            <button
              className={`btn-sm ${source.enabled ? "btn-ghost" : "btn-primary"}`}
              onClick={() => handleToggle(source)}
            >
              {source.enabled ? "Pausieren" : "Aktivieren"}
            </button>
            <button className="btn-ghost btn-sm" onClick={() => startEdit(source)}>
              Bearbeiten
            </button>
            {!isEmail && (
              <button className="btn-danger btn-sm" onClick={() => handleDelete(source)}>
                Löschen
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Welche Quellen sollen angezeigt werden?
  const visibleRss = rssOnly ? rssFeeds : [];
  const visibleEmail = rssOnly ? [] : emailSources;
  const hasAnySources = visibleRss.length > 0 || visibleEmail.length > 0;

  return (
    <div>
      {/* Hidden File Input fuer Logo-Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
        style={{ display: "none" }}
        onChange={handleLogoFile}
      />

      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">
            {rssOnly ? "RSS-Feeds" : "E-Mail-Quellen"}
          </h2>
          <p className="text-muted">
            {rssOnly
              ? "Verwalte deine RSS-Feeds. Neue Feeds werden automatisch abgerufen."
              : "E-Mail-Newsletter-Quellen verwalten."}
          </p>
        </div>
        {rssOnly && (
          <button className="btn-primary" onClick={() => { setShowAdd(true); setError(""); }}>
            + Feed hinzufügen
          </button>
        )}
      </div>

      {/* Neuen Feed anlegen */}
      {showAdd && (
        <div className="feed-add-card">
          <h3>Neuen RSS-Feed hinzufügen</h3>
          <div className="feed-form-row">
            <div className="feed-form-field feed-form-field-url">
              <label>Feed-URL *</label>
              <input
                type="url"
                placeholder="https://example.com/feed.xml"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                autoFocus
              />
            </div>
            <div className="feed-form-field feed-form-field-name">
              <label>Name (optional)</label>
              <input
                type="text"
                placeholder="Wird vom Feed übernommen"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </div>
          </div>
          {error && <p className="feed-form-error">{error}</p>}
          <div className="feed-form-actions">
            <button className="btn-primary" onClick={handleAdd} disabled={saving}>
              {saving ? "Wird gespeichert..." : "Hinzufügen"}
            </button>
            <button className="btn-ghost" onClick={() => setShowAdd(false)}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Quellen-Liste */}
      {loading ? (
        <p className="loading-text">Lade Quellen...</p>
      ) : !hasAnySources && !showAdd ? (
        <div className="feed-empty">
          <div className="feed-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 11a9 9 0 019 9M4 4a16 16 0 0116 16" />
              <circle cx="5" cy="19" r="1" />
            </svg>
          </div>
          <p>{rssOnly ? "Noch keine RSS-Feeds konfiguriert." : "Keine E-Mail-Quellen vorhanden."}</p>
          {rssOnly && (
            <button className="btn-primary" onClick={() => setShowAdd(true)}>
              Ersten Feed hinzufügen
            </button>
          )}
        </div>
      ) : (
        <>
          {/* E-Mail-Quellen (nur im Admin-Modus) */}
          {visibleEmail.length > 0 && (
            <>
              {visibleRss.length > 0 && <h3 className="feed-section-title">E-Mail-Newsletter</h3>}
              <div className="feed-list">
                {visibleEmail.map((s) => renderSourceCard(s, true))}
              </div>
            </>
          )}

          {/* RSS-Feeds */}
          {visibleRss.length > 0 && (
            <>
              {visibleEmail.length > 0 && <h3 className="feed-section-title">RSS-Feeds</h3>}
              <div className="feed-list">
                {visibleRss.map((s) => renderSourceCard(s, false))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
